import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, RefreshCcw, ShieldOff } from "lucide-react";
import { useState } from "react";
import { api, type ApiKeyMetadata } from "../api";
import { EmptyState, ErrorNotice, LoadingRows, ReasonDialog, SecretDialog, Status, formatTime } from "../components";

type Operation = { kind: "reset" | "disable"; item: ApiKeyMetadata } | null;

export function ApiKeysPage({ role }: { role: string }) {
  const queryClient = useQueryClient();
  const [operation, setOperation] = useState<Operation>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const keys = useQuery({ queryKey: ["api-keys"], queryFn: () => api<ApiKeyMetadata[]>("/admin/v1/api-keys") });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["api-keys"] });
  const create = useMutation({
    mutationFn: (reason: string) => api<{ api_key: string }>("/admin/v1/api-keys", {
      method: "POST",
      body: JSON.stringify({ scopes: ["jobs:read", "jobs:write"], expires_at: null, reason }),
    }),
    onSuccess: (data) => {
      setCreateOpen(false);
      setSecret(data.api_key);
      refresh();
    },
  });
  const change = useMutation({
    mutationFn: ({ operation: activeOperation, reason }: { operation: NonNullable<Operation>; reason: string }) => api<{ api_key?: string } | ApiKeyMetadata>(`/admin/v1/api-keys/${activeOperation.item.key_id}/${activeOperation.kind}`, { method: "POST", body: JSON.stringify({ reason }) }),
    onSuccess: (data) => {
      if ("api_key" in data && data.api_key) setSecret(data.api_key);
      setOperation(null);
      refresh();
    },
  });
  const activeCount = keys.data?.filter((item) => item.status === "ACTIVE").length ?? 0;
  const canCreate = role === "ADMIN" && activeCount < 2;
  const error = keys.error ?? create.error ?? change.error;

  return <div className="page">
    <header className="page-heading">
      <div><p className="eyebrow">ACCESS / API KEYS</p><h1>全局 API Key</h1><p>用于创建和查询转码任务。所有 Key 使用系统当前生效的存储与转码配置。</p></div>
      {role === "ADMIN" && <button className="button button--primary" disabled={!canCreate || create.isPending} title={activeCount >= 2 ? "最多同时保留两把有效 Key；请先禁用旧 Key" : undefined} onClick={() => setCreateOpen(true)}><Plus size={16} />创建 API Key</button>}
    </header>
    {error && <ErrorNotice error={error} />}
    {keys.isPending ? <LoadingRows columns={5} /> : keys.data?.length ? <div className="table-wrap"><table className="responsive-table"><thead><tr><th>API Key</th><th>权限</th><th>状态</th><th>最后使用</th><th>创建时间</th><th aria-label="操作" /></tr></thead><tbody>{keys.data.map((item) => <tr key={item.key_id}>
      <td data-label="API Key"><code>{item.key_prefix}…</code></td>
      <td data-label="权限">{describeScopes(item.scopes)}</td>
      <td data-label="状态"><Status value={item.status} /></td>
      <td data-label="最后使用">{formatTime(item.last_used_at)}</td>
      <td data-label="创建时间">{formatTime(item.created_at)}</td>
      <td className="responsive-table__actions">{role === "ADMIN" && item.status === "ACTIVE" && <div className="row-actions"><button className="icon-button" title="重置密钥" aria-label={`重置 ${item.key_prefix}`} onClick={() => setOperation({ kind: "reset", item })}><RefreshCcw size={16} /></button><button className="icon-button icon-button--danger" title="禁用密钥" aria-label={`禁用 ${item.key_prefix}`} onClick={() => setOperation({ kind: "disable", item })}><ShieldOff size={16} /></button></div>}</td>
    </tr>)}</tbody></table></div> : <EmptyState title="还没有全局 API Key"><KeyRound size={20} />创建后即可调用任务接口，完整 Key 只显示一次。</EmptyState>}
    <ReasonDialog open={createOpen} title="创建全局 API Key" target="转码任务 API" confirmLabel="创建并展示完整 Key" pending={create.isPending} onClose={() => setCreateOpen(false)} onConfirm={(reason) => create.mutate(reason)} />
    <ReasonDialog open={Boolean(operation)} title={operation?.kind === "reset" ? "重置 API Key" : "禁用 API Key"} target={operation?.item.key_prefix ?? ""} confirmLabel={operation?.kind === "reset" ? "重置并使旧 Key 失效" : "立即禁用 Key"} pending={change.isPending} onClose={() => setOperation(null)} onConfirm={(reason) => operation && change.mutate({ operation, reason })} />
    <SecretDialog secret={secret} onClose={() => setSecret(null)} />
  </div>;
}

function describeScopes(scopes: string[]) {
  if (scopes.includes("jobs:read") && scopes.includes("jobs:write")) return "创建与查询任务";
  if (scopes.includes("jobs:write")) return "创建任务";
  if (scopes.includes("jobs:read")) return "查询任务";
  return scopes.join(" · ");
}
